import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Alert, CircularProgress, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Stack, IconButton,
    Card, CardContent, Divider
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    Save as SaveIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    AddCircle as AddCircleIcon,
    Cancel as CancelIcon,
    Build as BuildIcon,
    PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import { getUltimoBalancete, getConfig, getDashboardData, runSimulation } from '../api/api';

const SimuladorPage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [feedback, setFeedback] = useState(null);

    // Dados base
    const [contasDisponiveis, setContasDisponiveis] = useState([]);
    const [centrosDisponiveis, setCentrosDisponiveis] = useState([]);
    const [resultadoBase, setResultadoBase] = useState(null);

    // Parâmetros de Simulação
    const [simulacaoCustos, setSimulacaoCustos] = useState([]);
    const [simulacaoProducao, setSimulacaoProducao] = useState([]);
    const [resultadoSimulado, setResultadoSimulado] = useState(null);

    // Inputs para adicionar nova simulação
    const [newCostSim, setNewCostSim] = useState({ conta: '', novo_valor: '' });
    const [newProdSim, setNewProdSim] = useState({ centro: '', nova_producao: '' });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const balanceteRes = await getUltimoBalancete();
                const contasUnicas = [...new Set(balanceteRes.data.data.map(item => item.Conta))];
                setContasDisponiveis(contasUnicas);

                const centrosRes = await getConfig('centros_custo');
                setCentrosDisponiveis(centrosRes.data.map(cc => cc.nome));

                const baseRes = await getDashboardData();
                if (baseRes.data.status === 'Erro') throw new Error(baseRes.data.mensagem);
                setResultadoBase(baseRes.data);

            } catch (err) {
                const msg = err.response?.data?.mensagem || "É necessário processar um mês (Importação de Dados) antes de usar o Simulador.";
                setError(msg);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleAddCostSim = () => {
        if (!newCostSim.conta || !newCostSim.novo_valor || isNaN(parseFloat(newCostSim.novo_valor))) {
            setFeedback({ message: 'Preencha a conta e um valor numérico válido.', type: 'error' });
            return;
        }
        if (simulacaoCustos.some(s => s.conta === newCostSim.conta)) {
            setFeedback({ message: 'Esta conta já está na lista de simulação. Remova antes de adicionar novamente.', type: 'warning' });
            return;
        }

        setSimulacaoCustos([...simulacaoCustos, { ...newCostSim, novo_valor: parseFloat(newCostSim.novo_valor) }]);
        setNewCostSim({ conta: '', novo_valor: '' });
        setFeedback({ message: 'Simulação de custo adicionada.', type: 'success' });
    };

    const handleRemoveCostSim = (conta) => {
        setSimulacaoCustos(simulacaoCustos.filter(s => s.conta !== conta));
        setFeedback({ message: `Simulação para a conta '${conta}' removida.`, type: 'warning' });
    };

    const handleAddProdSim = () => {
        if (!newProdSim.centro || !newProdSim.nova_producao || isNaN(parseFloat(newProdSim.nova_producao))) {
            setFeedback({ message: 'Preencha o centro de custo e um valor numérico válido para produção.', type: 'error' });
            return;
        }
        if (simulacaoProducao.some(s => s.centro === newProdSim.centro)) {
            setFeedback({ message: 'Este centro já está na lista de simulação de produção.', type: 'warning' });
            return;
        }

        setSimulacaoProducao([...simulacaoProducao, { ...newProdSim, nova_producao: parseFloat(newProdSim.nova_producao) }]);
        setNewProdSim({ centro: '', nova_producao: '' });
        setFeedback({ message: 'Simulação de produção adicionada.', type: 'success' });
    };

    const handleRemoveProdSim = (centro) => {
        setSimulacaoProducao(simulacaoProducao.filter(s => s.centro !== centro));
        setFeedback({ message: `Simulação de produção para '${centro}' removida.`, type: 'warning' });
    };

    const handleRunSimulation = async () => {
        setLoading(true);
        setResultadoSimulado(null);
        setError(null);

        const params = {
            custos: simulacaoCustos,
            producao: simulacaoProducao,
        };

        try {
            const res = await runSimulation(params);
            setResultadoSimulado(res.data);
            setFeedback({ message: 'Simulação concluída com sucesso! Veja os resultados abaixo.', type: 'success' });
        } catch (err) {
            const msg = err.response?.data?.mensagem || "Erro desconhecido ao rodar a simulação. Verifique o console do backend.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        if (value === undefined || value === null || isNaN(value)) return "R$ 0,00";
        return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const getUnitCost = (data, centro) => {
        const detail = data?.custo_unitario_detalhado?.[centro];
        return detail ? { custo: detail.custo, unidade: detail.unidade } : { custo: 0, unidade: 'N/A' };
    };

    const getProfit = (data, centro) => {
        return data?.relatorio_analitico?.[centro]?.Lucro || 0;
    };

    const isBetter = (simulatedValue, baseValue, isCost = true) => {
        if (isNaN(simulatedValue) || isNaN(baseValue)) return 'text.secondary';

        if (isCost) {
            return simulatedValue < baseValue ? 'success.main' : simulatedValue > baseValue ? 'error.main' : 'text.secondary';
        } else {
            return simulatedValue > baseValue ? 'success.main' : simulatedValue < baseValue ? 'error.main' : 'text.secondary';
        }
    };

    const centrosComparacao = new Set([
        ...(resultadoBase ? Object.keys(resultadoBase.relatorio_analitico || {}) : []),
        ...(resultadoSimulado ? Object.keys(resultadoSimulado.relatorio_analitico || {}) : []),
    ]);

    if (loading && !resultadoSimulado) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    if (error && !resultadoBase) return (
        <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ mr: 2, color: 'primary.main' }} /> Agente Simulador Estratégico ("What-if")
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Teste o impacto de alterações em custos ou produção. A simulação usa o último mês processado como base.
            </Typography>

            {feedback && (
                <Alert severity={feedback.type} sx={{ mb: 4 }}>
                    {feedback.message}
                </Alert>
            )}

            {/* SEÇÃO DE ENTRADA DE SIMULAÇÃO */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Variáveis de Simulação
                </Typography>

                {/* SIMULAÇÃO DE CUSTOS */}
                <Box sx={{ mb: 4, pb: 4, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <BuildIcon sx={{ mr: 1, color: 'text.secondary' }} /> 1. Alteração de Custos
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-end" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Conta a Simular (Baseado no Balancete)</InputLabel>
                                <Select
                                    value={newCostSim.conta}
                                    label="Conta a Simular (Baseado no Balancete)"
                                    onChange={(e) => setNewCostSim({ ...newCostSim, conta: e.target.value })}
                                >
                                    <MenuItem value=""><em>Selecione uma Conta...</em></MenuItem>
                                    {contasDisponiveis.map(conta => (
                                        <MenuItem key={conta} value={conta}>{conta}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Novo Valor Total (R$)"
                                type="number"
                                fullWidth
                                size="small"
                                value={newCostSim.novo_valor}
                                onChange={(e) => setNewCostSim({ ...newCostSim, novo_valor: e.target.value })}
                                placeholder="Ex: 5500.00"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleAddCostSim}
                                disabled={!newCostSim.conta || !newCostSim.novo_valor}
                                startIcon={<AddCircleIcon />}
                            >
                                Adicionar Custo
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Lista de Simulações de Custo */}
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        {simulacaoCustos.map(sim => (
                            <Box key={sim.conta} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight="bold">{sim.conta}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" color="primary" sx={{ mr: 2 }}>Novo Total: {formatCurrency(sim.novo_valor)}</Typography>
                                    <IconButton size="small" color="error" onClick={() => handleRemoveCostSim(sim.conta)}>
                                        <CancelIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                {/* SIMULAÇÃO DE PRODUÇÃO */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <BuildIcon sx={{ mr: 1, color: 'text.secondary' }} /> 2. Alteração de Produção
                    </Typography>
                    <Grid container spacing={2} alignItems="flex-end" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Centro de Custo (Gerencial)</InputLabel>
                                <Select
                                    value={newProdSim.centro}
                                    label="Centro de Custo (Gerencial)"
                                    onChange={(e) => setNewProdSim({ ...newProdSim, centro: e.target.value })}
                                >
                                    <MenuItem value=""><em>Selecione um Centro...</em></MenuItem>
                                    {centrosDisponiveis.map(centro => (
                                        <MenuItem key={centro} value={centro}>{centro}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Nova Produção (Unidades)"
                                type="number"
                                fullWidth
                                size="small"
                                value={newProdSim.nova_producao}
                                onChange={(e) => setNewProdSim({ ...newProdSim, nova_producao: e.target.value })}
                                placeholder="Ex: 1200"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleAddProdSim}
                                disabled={!newProdSim.centro || !newProdSim.nova_producao}
                                startIcon={<AddCircleIcon />}
                            >
                                Adicionar Produção
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Lista de Simulações de Produção */}
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        {simulacaoProducao.map(sim => (
                            <Box key={sim.centro} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <Typography variant="body2" fontWeight="bold">{sim.centro}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" color="primary" sx={{ mr: 2 }}>Nova Produção: {sim.nova_producao.toLocaleString('pt-BR')}</Typography>
                                    <IconButton size="small" color="error" onClick={() => handleRemoveProdSim(sim.centro)}>
                                        <CancelIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                {/* BOTÃO DE RODAR SIMULAÇÃO */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={handleRunSimulation}
                        disabled={loading || (simulacaoCustos.length === 0 && simulacaoProducao.length === 0)}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                    >
                        {loading ? 'Processando Simulação...' : 'Rodar Simulação Estratégica'}
                    </Button>
                </Box>
            </Paper>

            {/* SEÇÃO DE RESULTADOS (COMPARATIVO) */}
            {resultadoSimulado && resultadoBase && (
                <Paper sx={{ p: 3, border: 1, borderColor: 'success.main' }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'success.dark', display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon sx={{ mr: 1 }} /> Comparativo de Impacto da Simulação
                    </Typography>

                    <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
                        <Typography variant="caption" display="block">Base da Simulação: Resultado Real do Mês</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6">Custo Total Global (Real): {formatCurrency(resultadoBase.custo_total_ano / 12)}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" fontWeight="bold">Custo Total Global (Simulado): {formatCurrency(resultadoSimulado.custo_total_ano / 12)}</Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell width="25%">Centro de Custo</TableCell>
                                    <TableCell>Custo Unitário BASE</TableCell>
                                    <TableCell>Custo Unitário SIMULADO</TableCell>
                                    <TableCell>Lucro/Prejuízo BASE</TableCell>
                                    <TableCell>Lucro/Prejuízo SIMULADO</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...centrosComparacao].map(centro => {
                                    const baseUnit = getUnitCost(resultadoBase, centro);
                                    const simulatedUnit = getUnitCost(resultadoSimulado, centro);
                                    const baseProfit = getProfit(resultadoBase, centro);
                                    const simulatedProfit = getProfit(resultadoSimulado, centro);

                                    return (
                                        <TableRow key={centro} hover>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{centro}</TableCell>

                                            <TableCell sx={{ color: 'text.secondary' }}>
                                                {formatCurrency(baseUnit.custo)} / {baseUnit.unidade}
                                            </TableCell>

                                            <TableCell sx={{ color: isBetter(simulatedUnit.custo, baseUnit.custo, true), fontWeight: 'bold' }}>
                                                {formatCurrency(simulatedUnit.custo)} / {simulatedUnit.unidade}
                                            </TableCell>

                                            <TableCell sx={{ color: 'text.secondary' }}>
                                                {formatCurrency(baseProfit)}
                                            </TableCell>

                                            <TableCell sx={{ color: isBetter(simulatedProfit, baseProfit, false), fontWeight: 'bold' }}>
                                                {formatCurrency(simulatedProfit)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Mensagem de Aviso se faltar resultado base */}
            {error && resultadoBase === null && (
                <Alert severity="error" sx={{ mt: 4 }}>
                    {error}
                </Alert>
            )}
        </Box>
    );
};

export default SimuladorPage;
